/**
 * MobileCampaignSelector - Unified Campaign Selector Component
 *
 * Supports two display modes:
 * - 'modal': Full-screen modal with overlay (used in MobileCalendar)
 * - 'inline': Inline component (used in MobileStrategy)
 *
 * Supports two selection modes:
 * - 'multiple': Multiple campaign selection (used in MobileCalendar)
 * - 'single': Single campaign selection (used in MobileStrategy)
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import './MobileCampaignSelector.css';
import useLanguage from '../../../hooks/useLanguage';

const MobileCampaignSelector = ({
  // Data
  campaigns = [],

  // Display mode
  mode = 'modal', // 'modal' | 'inline'

  // Selection mode
  selectionMode = 'multiple', // 'multiple' | 'single'

  // Multiple selection props (when selectionMode='multiple')
  selectedCampaignIds = [],
  onCampaignSelectionChange,

  // Single selection props (when selectionMode='single')
  activeCampaign = null,
  onCampaignSelect,

  // UI options
  showAllOption = true,
  requireConfirmation = true, // true = show Apply button, false = notify immediately

  // Modal props (only when mode='modal')
  isOpen = false,
  onClose
}) => {
  const { t, language } = useLanguage();
  // Local state for selection (used only when requireConfirmation=true)
  const [localSelectedIds, setLocalSelectedIds] = useState(selectedCampaignIds);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Dropdown state
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

  // Refs for dropdown
  const periodDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Display campaigns (API-provided)
  const displayCampaigns = campaigns;

  // Sync local selection with external selection
  useEffect(() => {
    if (selectionMode === 'multiple') {
      // Check if array content is different to avoid unnecessary updates
      const isDifferent = localSelectedIds.length !== selectedCampaignIds.length ||
        localSelectedIds.some((id, index) => id !== selectedCampaignIds[index]);

      if (isDifferent) {
        setLocalSelectedIds(selectedCampaignIds);
      }
    } else if (selectionMode === 'single') {
      if (activeCampaign) {
        // Only update if the ID is actually different
        if (localSelectedIds.length !== 1 || localSelectedIds[0] !== activeCampaign.id) {
          setLocalSelectedIds([activeCampaign.id]);
        }
      } else {
        // Only clear if not already empty
        if (localSelectedIds.length > 0) {
          setLocalSelectedIds([]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode, selectedCampaignIds, activeCampaign?.id]); // localSelectedIds read but not in deps to prevent loop

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(event.target)) {
        setIsPeriodDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter campaigns by period and status
  const getFilteredCampaigns = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
    const ninetyDaysAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));

    let filtered = displayCampaigns;

    // Filter by period
    switch (selectedPeriod) {
      case 'last30':
        filtered = filtered.filter(campaign => {
          const startDateValue = campaign.start_date || campaign.startDate;
          const dateString = (typeof startDateValue === 'object' && startDateValue.$date)
            ? startDateValue.$date
            : startDateValue;

          const startDate = new Date(dateString);
          return startDate >= thirtyDaysAgo;
        });
        break;
      case 'last90':
        filtered = filtered.filter(campaign => {
          const startDateValue = campaign.start_date || campaign.startDate;
          const dateString = (typeof startDateValue === 'object' && startDateValue.$date)
            ? startDateValue.$date
            : startDateValue;

          const startDate = new Date(dateString);
          return startDate >= ninetyDaysAgo;
        });
        break;
      default:
        break;
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(campaign => campaign.status === selectedStatus);
    }

    return filtered;
  };

  const filteredCampaigns = getFilteredCampaigns();
  const selectedCount = localSelectedIds.length;
  const isAllSelected = selectedCount === filteredCampaigns.length && filteredCampaigns.length > 0;

  const applyButtonLabel = useMemo(() => {
    if (selectionMode === 'single') {
      return selectedCount === 0
        ? t('campaignSelector.actions.selectCampaign')
        : t('campaignSelector.actions.apply');
    }

    if (selectedCount === 0) {
      return t('campaignSelector.actions.applyAll');
    }

    return t('campaignSelector.actions.applyCount', { count: selectedCount });
  }, [selectionMode, selectedCount, t]);

  // Handle "All Campaigns" click (only for multiple selection)
  const handleAllCampaignsClick = useCallback(() => {
    if (selectionMode === 'single') return; // Disabled in single selection mode

    const newSelection = isAllSelected ? [] : filteredCampaigns.map(campaign => campaign.id);
    setLocalSelectedIds(newSelection);

    // If requireConfirmation=false, notify parent immediately
    if (!requireConfirmation && onCampaignSelectionChange) {
      const selectionType = isAllSelected ? 'clear' : 'all';
      onCampaignSelectionChange(newSelection, selectionType);
    }
  }, [selectionMode, filteredCampaigns, isAllSelected, requireConfirmation, onCampaignSelectionChange]);

  // Handle campaign click
  const handleCampaignClick = useCallback((campaign) => {
    if (selectionMode === 'single') {
      // Single selection mode
      const newSelection = [campaign.id];
      setLocalSelectedIds(newSelection);

      // Notify parent (immediately or on apply)
      if (requireConfirmation) {
        // Wait for apply button
      } else if (onCampaignSelect) {
        onCampaignSelect(campaign);
      }
    } else {
      // Multiple selection mode
      let newSelection;

      if (localSelectedIds.includes(campaign.id)) {
        // Deselect campaign
        newSelection = localSelectedIds.filter(id => id !== campaign.id);
      } else {
        // Select campaign
        newSelection = [...localSelectedIds, campaign.id];
      }

      setLocalSelectedIds(newSelection);

      // If requireConfirmation=false, notify parent immediately
      if (!requireConfirmation && onCampaignSelectionChange) {
        onCampaignSelectionChange(newSelection, 'specific');
      }
    }
  }, [selectionMode, localSelectedIds, requireConfirmation, onCampaignSelect, onCampaignSelectionChange]);

  // Clear all selections (only for multiple selection)
  const handleClearAll = useCallback(() => {
    if (selectionMode === 'single') return; // Not supported in single selection mode

    setLocalSelectedIds([]);

    // If requireConfirmation=false, notify parent immediately
    if (!requireConfirmation && onCampaignSelectionChange) {
      onCampaignSelectionChange([], 'clear');
    }
  }, [selectionMode, requireConfirmation, onCampaignSelectionChange]);

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setIsPeriodDropdownOpen(false);
    // Reset selection when filter changes (only if not using confirmation)
    if (!requireConfirmation && selectionMode === 'multiple') {
      setLocalSelectedIds([]);
      if (onCampaignSelectionChange) {
        onCampaignSelectionChange([], 'period_change');
      }
    }
  };

  // Handle status change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setIsStatusDropdownOpen(false);
    // Reset selection when filter changes (only if not using confirmation)
    if (!requireConfirmation && selectionMode === 'multiple') {
      setLocalSelectedIds([]);
      if (onCampaignSelectionChange) {
        onCampaignSelectionChange([], 'status_change');
      }
    }
  };

  // Handle Apply button click (only when requireConfirmation=true)
  const handleApply = useCallback(() => {
    console.log('[MobileCampaignSelector] Applying selection:', {
      selectionMode,
      selectedIds: localSelectedIds,
      count: localSelectedIds.length,
      isAll: localSelectedIds.length === filteredCampaigns.length && filteredCampaigns.length > 0
    });

    if (selectionMode === 'multiple' && onCampaignSelectionChange) {
      // Determine selection type
      let selectionType = 'specific';
      if (localSelectedIds.length === 0) {
        selectionType = 'all'; // No selection = all campaigns
      } else if (localSelectedIds.length === filteredCampaigns.length && filteredCampaigns.length > 0) {
        selectionType = 'all'; // All filtered campaigns selected = all
      }

      // Notify parent with final selection
      onCampaignSelectionChange(localSelectedIds, selectionType);
    } else if (selectionMode === 'single' && onCampaignSelect) {
      // Find selected campaign and notify parent
      const selectedCampaign = filteredCampaigns.find(c => c.id === localSelectedIds[0]);
      if (selectedCampaign) {
        onCampaignSelect(selectedCampaign);
      }
    }

    // Close modal if in modal mode
    if (mode === 'modal' && onClose) {
      onClose();
    }
  }, [selectionMode, localSelectedIds, filteredCampaigns, onCampaignSelectionChange, onCampaignSelect, mode, onClose]);

  const locale = useMemo(() => {
    if (language === 'en') return 'en-US';
    if (language === 'it') return 'it-IT';
    return language || 'it-IT';
  }, [language]);

  // Period and status options (localized)
  const periodOptions = useMemo(() => ([
    { value: 'all', label: t('campaignSelector.filters.period.all') },
    { value: 'last30', label: t('campaignSelector.filters.period.last30') },
    { value: 'last90', label: t('campaignSelector.filters.period.last90') }
  ]), [t]);

  const statusOptions = useMemo(() => ([
    { value: 'all', label: t('campaignSelector.filters.status.all') },
    { value: 'draft', label: t('campaignSelector.filters.status.draft') },
    { value: 'active', label: t('campaignSelector.filters.status.active') },
    { value: 'paused', label: t('campaignSelector.filters.status.paused') },
    { value: 'completed', label: t('campaignSelector.filters.status.completed') }
  ]), [t]);

  const statusLabelMap = useMemo(() => ({
    draft: t('campaignSelector.status.draft'),
    active: t('campaignSelector.status.active'),
    paused: t('campaignSelector.status.paused'),
    completed: t('campaignSelector.status.completed')
  }), [t]);

  const getStatusLabel = useCallback((status) => statusLabelMap[status] || status || '', [statusLabelMap]);

  const selectedPeriodLabel = useMemo(() => (
    periodOptions.find(opt => opt.value === selectedPeriod)?.label || periodOptions[0]?.label || ''
  ), [periodOptions, selectedPeriod]);

  const selectedStatusLabel = useMemo(() => (
    statusOptions.find(opt => opt.value === selectedStatus)?.label || statusOptions[0]?.label || ''
  ), [statusOptions, selectedStatus]);

  const startDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' });
    } catch (error) {
      console.warn('[MobileCampaignSelector] Falling back to it-IT start date formatter', error);
      return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short' });
    }
  }, [locale]);

  const endDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (error) {
      console.warn('[MobileCampaignSelector] Falling back to it-IT end date formatter', error);
      return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }, [locale]);

  const formatCampaignDateRange = useCallback((startDateValue, endDateValue) => {
    const parseDate = (value) => {
      if (!value) {
        return null;
      }

      let dateString = value;
      if (typeof value === 'object' && value.$date) {
        dateString = value.$date;
      }

      const parsedDate = new Date(dateString);
      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      if (typeof dateString === 'string' && dateString.endsWith('Z')) {
        const year = parsedDate.getUTCFullYear();
        const month = parsedDate.getUTCMonth();
        const day = parsedDate.getUTCDate();
        const hours = parsedDate.getUTCHours();
        const minutes = parsedDate.getUTCMinutes();
        const seconds = parsedDate.getUTCSeconds();
        return new Date(year, month, day, hours, minutes, seconds);
      }

      return parsedDate;
    };

    const startDate = parseDate(startDateValue);
    const endDate = parseDate(endDateValue);

    if (!startDate || !endDate) {
      return t('campaignSelector.cards.datesUnavailable');
    }

    try {
      const formattedStart = startDateFormatter.format(startDate);
      const formattedEnd = endDateFormatter.format(endDate);
      return t('campaignSelector.cards.dateRange', { start: formattedStart, end: formattedEnd });
    } catch (error) {
      console.warn('[MobileCampaignSelector] Failed to format date range', error);
      return t('campaignSelector.cards.datesUnavailable');
    }
  }, [endDateFormatter, startDateFormatter, t]);

  // Don't render if modal mode and not open
  if (mode === 'modal' && !isOpen) return null;

  // Render content
  const renderContent = () => (
    <div className={`mobile-campaign-selector ${mode === 'inline' ? 'inline-mode' : ''}`} onClick={(e) => mode === 'modal' && e.stopPropagation()}>
      {/* Header */}
      <div className="mobile-campaign-selector-header">
        <h3>{t('campaignSelector.header.title')}</h3>
        {mode === 'modal' && (
          <button
            className="close-button"
            onClick={onClose}
            aria-label={t('campaignSelector.header.close')}
            title={t('campaignSelector.header.close')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
        )}
      </div>

      {/* Content */}
      <div className="mobile-campaign-navbar-content">
        {/* Filters */}
        <div className="mobile-filters-container">
          {/* Period selector */}
          <div className="mobile-period-selector" ref={periodDropdownRef}>
            <button
              className="mobile-period-select"
              onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
            >
              <span>{selectedPeriodLabel}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            {isPeriodDropdownOpen && (
              <div className="mobile-dropdown-menu">
                {periodOptions.map(option => (
                  <button
                    key={option.value}
                    className={`mobile-dropdown-item ${selectedPeriod === option.value ? 'active' : ''}`}
                    onClick={() => handlePeriodChange(option.value)}
                  >
                    {option.label}
                    {selectedPeriod === option.value && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Status selector */}
          <div className="mobile-status-selector" ref={statusDropdownRef}>
            <button
              className="mobile-status-select"
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            >
              <span>{selectedStatusLabel}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="mobile-dropdown-menu">
                {statusOptions.map(option => (
                  <button
                    key={option.value}
                    className={`mobile-dropdown-item ${selectedStatus === option.value ? 'active' : ''}`}
                    onClick={() => handleStatusChange(option.value)}
                  >
                    {option.label}
                    {selectedStatus === option.value && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Campaigns grid */}
        <div className="mobile-campaigns-grid">
          {filteredCampaigns.map((campaign) => {
            const isSelected = localSelectedIds.includes(campaign.id);

            return (
              <div
                key={campaign.id}
                className={`mobile-campaign-card ${isSelected ? 'selected' : ''} ${campaign.status} ${selectionMode === 'single' ? 'single-select' : ''}`}
                onClick={() => handleCampaignClick(campaign)}
                title={selectionMode === 'single'
                  ? t(
                    isSelected
                      ? 'campaignSelector.cards.tooltip.singleSelected'
                      : 'campaignSelector.cards.tooltip.singlePrompt',
                    { name: campaign.name }
                  )
                  : t(
                    isSelected
                      ? 'campaignSelector.cards.tooltip.multiSelected'
                      : 'campaignSelector.cards.tooltip.multiPrompt',
                    { name: campaign.name }
                  )
                }
              >
                <div className="mobile-campaign-card-content">
                  <div className="mobile-campaign-title-row">
                    <h3 className="mobile-campaign-name">{campaign.name}</h3>
                    <div className="mobile-selection-checkbox">
                      {isSelected && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Status badge and dates row */}
                    <div className="mobile-campaign-info-row">
                      <div className={`mobile-campaign-status-badge status-${campaign.status || 'draft'}`}>
                        {getStatusLabel(campaign.status || 'draft')}
                      </div>

                      <div className="mobile-campaign-dates">
                        {formatCampaignDateRange(
                          campaign.start_date || campaign.startDate,
                          campaign.end_date || campaign.endDate
                        )}
                    </div>
                  </div>
                </div>

                <div className="mobile-campaign-card-overlay"></div>
              </div>
            );
          })}
        </div>

        {filteredCampaigns.length === 0 && (
          <div className="mobile-no-campaigns">
            <p>{t('campaignSelector.empty.message')}</p>
          </div>
        )}
      </div>

      {/* Footer with actions (only when requireConfirmation=true) */}
      {requireConfirmation && (
        <div className="mobile-campaign-selector-footer">
          {/* Action buttons */}
          <div className="mobile-footer-actions">
            {/* "All campaigns" button (only for multiple selection) */}
            {showAllOption && selectionMode === 'multiple' && (
              <button
                className={`mobile-footer-action-btn ${isAllSelected ? 'active' : ''}`}
                onClick={handleAllCampaignsClick}
                title={t('campaignSelector.actions.selectAllTooltip')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
                  <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor" opacity="0.9"/>
                  <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor" opacity="0.9"/>
                  <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
                </svg>
                <span>{t('campaignSelector.actions.all')}</span>
              </button>
            )}

            {/* "Clear" button (only for multiple selection) */}
            {selectedCount > 0 && selectionMode === 'multiple' && (
              <button
                className="mobile-footer-action-btn"
                onClick={handleClearAll}
                title={t('campaignSelector.actions.clearTooltip')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>{t('campaignSelector.actions.clear')}</span>
              </button>
            )}
          </div>

          {/* Apply button */}
          <button
            className="mobile-apply-button"
            onClick={handleApply}
          >
            {applyButtonLabel}
          </button>
        </div>
      )}
    </div>
  );

  // Wrap in overlay if modal mode
  if (mode === 'modal') {
    return (
      <div className="mobile-campaign-selector-overlay" onClick={onClose}>
        {renderContent()}
      </div>
    );
  }

  // Return unwrapped content if inline mode
  return renderContent();
};

export default MobileCampaignSelector;
